/**
 * Category Suggestion Service (T081)
 * 
 * API client for AI-powered category suggestion endpoint.
 * POST /api/questionnaires/suggest-category/
 */

export interface CategorySuggestion {
  suggested_category: 'marriage_relationship' | 'stress_anxiety' | 'not_defined'
  confidence: number
  reasoning: string
  keywords_detected: string[]
  fallback_to_manual: boolean
  error?: string
}

class CategorySuggestionService {
  private baseURL = '/api/questionnaires'

  /**
   * Get AI-powered category suggestion for a conversation.
   * 
   * @param conversationId - ID of the conversation to analyze
   * @returns CategorySuggestion with AI's recommendation
   * @throws Error if request fails or user is not authenticated
   */
  async suggestCategory(conversationId: number): Promise<CategorySuggestion> {
    try {
      const token = localStorage.getItem('access_token')
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`${this.baseURL}/suggest-category/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ conversation_id: conversationId })
      })

      // Handle different HTTP status codes per API contract
      if (!response.ok) {
        await response.json().catch(() => ({}))

        switch (response.status) {
          case 401:
            throw new Error('Authentication required')
          case 403:
            throw new Error('Permission denied')
          case 404:
            throw new Error('Conversation not found')
          case 400:
            throw new Error('Invalid request')
          default:
            throw new Error(`API error: ${response.status}`)
        }
      }

      const data: CategorySuggestion = await response.json()
      return data

    } catch (error) {
      if (error instanceof Error) {
        // Re-throw known errors
        if (error.message.includes('Authentication') ||
            error.message.includes('Permission') ||
            error.message.includes('not found') ||
            error.message.includes('Invalid request') ||
            error.message.includes('API error')) {
          throw error
        }
      }

      // Network or other errors
      throw new Error('Failed to fetch category suggestion')
    }
  }
}

export const categorySuggestionService = new CategorySuggestionService()
